// src/utils/bible/metadata.js
// Handles all book name / index / metadata resolution.
// Not meant to be imported directly by page components — use bibleService.js.

// ─── Module-level caches ──────────────────────────────────────────────────────
// Both are keyed by versionId so switching versions never returns stale results.
const metadataCache = new Map(); // versionId → validBooks[]
const bookMatchCache = new Map(); // "bookName:query:versionId" → boolean

// ─── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Attempts to pull a books array out of whatever shape the raw Bible data
 * happens to be in. Returns an array or null — never throws.
 */
function extractBookMetadataFromData(bibleData) {
  if (!bibleData || typeof bibleData !== "object") return null;

  try {
    // Shape 1: bibleData.metadata.books
    if (bibleData.metadata?.books) {
      const books = bibleData.metadata.books;

      if (books.value) {
        if (Array.isArray(books.value)) return books.value;
        if (typeof books.value === "object") {
          const values = Object.values(books.value);
          return values.length > 0 && Array.isArray(values[0]) ? values[0] : values;
        }
      }

      if (Array.isArray(books)) return books.length > 0 && Array.isArray(books[0]) ? books[0] : books;

      if (typeof books === "object") {
        const values = Object.values(books);
        return values.length > 0 && Array.isArray(values[0]) ? values[0] : values;
      }

      return books;
    }

    // Shape 2: bibleData.bookMetadata
    if (bibleData.bookMetadata) {
      const books = bibleData.bookMetadata;
      if (Array.isArray(books)) return books;
      if (typeof books === "object") return Object.values(books);
      return books;
    }

    // Shape 3: bibleData.books
    if (bibleData.books) {
      const books = bibleData.books;
      if (Array.isArray(books)) return books;
      if (typeof books === "object") return Object.values(books);
    }
  } catch (err) {
    console.warn("[bibleBookMetadata] extractBookMetadataFromData failed:", err);
  }

  return null;
}

/**
 * Returns true if the book object has at least one usable identifier.
 */
function isValidBookEntry(book) {
  return book !== null && typeof book === "object" && (book.english || book.kannada || book.code);
}

/**
 * Loose string similarity — not edit-distance, but good enough for fuzzy
 * book name matching. Returns a value between 0 and 1.
 */
function calculateSimilarity(str1, str2) {
  if (!str1 || !str2) return 0;
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;
  if (longer.length === 0) return 1.0;
  let matches = 0;
  for (let i = 0; i < shorter.length; i++) {
    if (longer.includes(shorter[i])) matches++;
  }
  return matches / longer.length;
}

/**
 * Core matching logic — checks whether a book metadata object matches
 * a given query string (English, Kannada, code, or aliases).
 */
function bookEntryMatchesQuery(book, originalQuery, lowerQuery) {
  if (!book || !originalQuery) return false;

  if (book.english) {
    const engLower = String(book.english).toLowerCase().trim();
    if (engLower === lowerQuery || engLower.includes(lowerQuery) || lowerQuery.includes(engLower)) return true;
  }

  if (book.kannada) {
    const kannadaName = String(book.kannada);
    if (kannadaName === originalQuery) return true;
    const kanLower = kannadaName.toLowerCase().trim();
    if (kanLower === lowerQuery || kanLower.includes(lowerQuery) || lowerQuery.includes(kanLower)) return true;
  }

  if (book.code) {
    const codeLower = String(book.code).toLowerCase().trim();
    if (codeLower === lowerQuery || codeLower.includes(lowerQuery) || lowerQuery.includes(codeLower)) return true;
  }

  if (Array.isArray(book.aliases)) {
    for (const alias of book.aliases) {
      if (!alias) continue;
      if (alias === originalQuery) return true;
      const aliasLower = String(alias).toLowerCase().trim();
      if (aliasLower === lowerQuery || aliasLower.includes(lowerQuery) || lowerQuery.includes(aliasLower)) return true;
    }
  }

  return false;
}

// ─── Exported functions ───────────────────────────────────────────────────────

/**
 * Extracts and caches valid book metadata from a Bible data object.
 * Returns an array of book objects, or null if none found.
 */
export async function extractBookMetadata(bibleData, versionId = null) {
  if (!bibleData) return null;

  const cacheKey = versionId || "default";
  if (metadataCache.has(cacheKey)) return metadataCache.get(cacheKey);

  try {
    const raw = extractBookMetadataFromData(bibleData);
    if (!raw) return null;

    const books = Array.isArray(raw) ? raw : Object.values(raw);
    const validBooks = books.filter(isValidBookEntry);

    if (validBooks.length > 0) {
      metadataCache.set(cacheKey, validBooks);
      return validBooks;
    }
  } catch (err) {
    console.error("[bibleBookMetadata] extractBookMetadata failed:", err);
  }

  return null;
}

/**
 * Finds a single book's metadata object by name (English, Kannada, code,
 * displayName, book_name, name, or alias). Returns null if not found.
 */
export async function getBookMetadataFromData(bibleData, bookName, versionId = null, preExtractedMetadata = null) {
  if (!bibleData || !bookName) return null;

  try {
    const metadata = preExtractedMetadata || (await extractBookMetadata(bibleData, versionId));
    if (!metadata) return null;

    const lowerBookName = String(bookName).toLowerCase().trim();
    const searchIn = Array.isArray(metadata) ? metadata : Object.values(metadata);

    const found = searchIn.find((book) => {
      if (!book) return false;
      if (book.kannada === bookName) return true;
      return (
        book.english?.toLowerCase() === lowerBookName ||
        book.kannada?.toLowerCase() === lowerBookName ||
        book.code?.toLowerCase() === lowerBookName ||
        book.displayName?.toLowerCase() === lowerBookName ||
        book.book_name?.toLowerCase() === lowerBookName ||
        book.name?.toLowerCase() === lowerBookName ||
        (Array.isArray(book.aliases) && book.aliases.some((alias) => alias === bookName || String(alias).toLowerCase() === lowerBookName))
      );
    });

    return found || null;
  } catch (err) {
    console.error("[bibleBookMetadata] getBookMetadataFromData failed:", err);
    return null;
  }
}

/**
 * Builds a Map of bookIndex → book_name from a Bible data object.
 * Iterates the verse array exactly once. The result is not cached here —
 * callers that need it repeatedly should hold onto the returned Map themselves.
 *
 * Returns an empty Map (never null) so callers can always call .get() safely.
 */
export function buildBookIndexMap(bibleData) {
  const map = new Map();
  if (!bibleData) return map;

  try {
    const verses = bibleData.verses || bibleData;
    if (!Array.isArray(verses)) return map;

    for (const v of verses) {
      if (!v || v.book === undefined || v.book === null) continue;
      const index = Number(v.book);
      if (!Number.isFinite(index)) continue;
      if (!map.has(index)) {
        map.set(index, v.book_name || null);
      }
    }
  } catch (err) {
    console.error("[bibleBookMetadata] buildBookIndexMap failed:", err);
  }

  return map;
}

/**
 * Resolves a numeric book index to a book name using a pre-built index map.
 * Call buildBookIndexMap() once and pass the result here for repeated lookups.
 *
 * Falls back to a full verse scan if no map is provided, for backwards
 * compatibility with any callers that haven't been updated yet.
 */
export function getBookNameByIndex(bookIndexMap, bookIndex) {
  if (!bookIndexMap || bookIndex === null || bookIndex === undefined) return null;

  try {
    // Accept either a pre-built Map or a raw bibleData object for compatibility
    if (bookIndexMap instanceof Map) {
      const result = bookIndexMap.get(Number(bookIndex));
      return result ?? null;
    }

    // Legacy fallback: bookIndexMap is actually a bibleData object
    // Logs a warning so callers know to upgrade to the map-based approach
    console.warn(
      "[bibleBookMetadata] getBookNameByIndex: received bibleData instead of a Map. " +
        "Call buildBookIndexMap(bibleData) once and pass the result instead."
    );
    const builtMap = buildBookIndexMap(bookIndexMap);
    return builtMap.get(Number(bookIndex)) ?? null;
  } catch (err) {
    console.error("[bibleBookMetadata] getBookNameByIndex failed:", err);
    return null;
  }
}

/**
 * Synchronous version of findBookMetadataByQuery — works on a pre-extracted
 * metadata array so it can be called from inside tight loops without awaiting.
 */
export function findBookMetadataByQueryInArray(metadataArray, query) {
  if (!Array.isArray(metadataArray) || !query) return null;

  try {
    const originalQuery = String(query).trim();
    const lowerQuery = originalQuery.toLowerCase().trim().replace(/\s+/g, " ");

    return metadataArray.find((book) => (isValidBookEntry(book) ? bookEntryMatchesQuery(book, originalQuery, lowerQuery) : false)) || null;
  } catch (err) {
    console.error("[bibleBookMetadata] findBookMetadataByQueryInArray failed:", err);
    return null;
  }
}

/**
 * Async version — extracts metadata itself then searches. Falls back to
 * fuzzy similarity matching if exact / substring matching finds nothing.
 */
async function findBookMetadataByQuery(bibleData, query, versionId = null) {
  if (!bibleData || !query) return null;

  try {
    const metadata = await extractBookMetadata(bibleData, versionId);
    if (!metadata) return null;

    const originalQuery = String(query).trim();
    const lowerQuery = originalQuery.toLowerCase().trim().replace(/\s+/g, " ");

    let books = Array.isArray(metadata) ? metadata : Object.values(metadata);
    books = books.filter(isValidBookEntry);
    if (books.length === 0) return null;

    // Pass 1: exact / substring / alias matching
    const found = books.find((book) => bookEntryMatchesQuery(book, originalQuery, lowerQuery));
    if (found) return found;

    // Pass 2: fuzzy similarity fallback (only for longer queries)
    if (lowerQuery.length < 2) return null;

    return (
      books.find((book) => {
        if (book.english) {
          const engLower = String(book.english).toLowerCase().trim();
          const minLen = Math.min(lowerQuery.length, engLower.length, 3);
          if (
            minLen >= 2 &&
            (engLower.includes(lowerQuery.substring(0, minLen)) ||
              lowerQuery.includes(engLower.substring(0, minLen)) ||
              calculateSimilarity(lowerQuery, engLower) > 0.7)
          )
            return true;
        }
        if (book.kannada) {
          const kanLower = String(book.kannada).toLowerCase().trim();
          const minLen = Math.min(lowerQuery.length, kanLower.length, 2);
          if (
            minLen >= 1 &&
            (kanLower.includes(lowerQuery) || lowerQuery.includes(kanLower) || calculateSimilarity(lowerQuery, kanLower) > 0.6)
          )
            return true;
        }
        return false;
      }) || null
    );
  } catch (err) {
    console.error("[bibleBookMetadata] findBookMetadataByQuery failed:", err);
    return null;
  }
}

/**
 * Returns true if a stored book name matches a user-supplied query.
 * Checks direct string inclusion first (fast path), then falls back to full
 * metadata comparison. Results are cached per (bookName, query, versionId).
 */
export async function bookMatchesQuery(bookName, query, bibleData = null, versionId = null, preExtractedMetadata = null) {
  if (!bookName || !query) return false;

  const lowerQuery = String(query).toLowerCase().trim();
  const lowerBookName = String(bookName).toLowerCase().trim();

  // Include versionId in the cache key so switching versions busts results
  const cacheKey = `${lowerBookName}:${lowerQuery}:${versionId || "none"}`;
  if (bookMatchCache.has(cacheKey)) return bookMatchCache.get(cacheKey);

  const setCache = (val) => {
    bookMatchCache.set(cacheKey, val);
    return val;
  };

  try {
    // Fast path: direct string containment
    if (lowerBookName.includes(lowerQuery) || lowerQuery.includes(lowerBookName)) return setCache(true);

    if (!bibleData) return setCache(false);

    // Resolve metadata for the stored book name
    const verseMetadata = preExtractedMetadata
      ? preExtractedMetadata.find(
          (book) =>
            book &&
            (book.kannada === bookName ||
              book.english?.toLowerCase() === lowerBookName ||
              book.kannada?.toLowerCase() === lowerBookName ||
              book.code?.toLowerCase() === lowerBookName)
        )
      : await getBookMetadataFromData(bibleData, bookName, versionId, preExtractedMetadata);

    if (verseMetadata) {
      if (
        verseMetadata.english?.toLowerCase().includes(lowerQuery) ||
        verseMetadata.kannada?.toLowerCase().includes(lowerQuery) ||
        verseMetadata.code?.toLowerCase().includes(lowerQuery) ||
        (Array.isArray(verseMetadata.aliases) && verseMetadata.aliases.some((a) => String(a).toLowerCase().includes(lowerQuery)))
      )
        return setCache(true);
    }

    // Resolve metadata for the query itself
    const queryMetadata = preExtractedMetadata
      ? findBookMetadataByQueryInArray(preExtractedMetadata, query)
      : await findBookMetadataByQuery(bibleData, query, versionId);

    if (queryMetadata) {
      const qKannada = queryMetadata.kannada;
      const qKannadaLower = qKannada?.toLowerCase();
      const qEnglishLower = queryMetadata.english?.toLowerCase();

      if (
        qKannada === bookName ||
        qKannadaLower === lowerBookName ||
        qEnglishLower === lowerBookName ||
        queryMetadata.code?.toLowerCase() === lowerBookName ||
        (qKannada && (bookName.includes(qKannada) || qKannada.includes(bookName))) ||
        (qKannadaLower && (lowerBookName.includes(qKannadaLower) || qKannadaLower.includes(lowerBookName))) ||
        (qEnglishLower && lowerBookName.includes(qEnglishLower))
      )
        return setCache(true);
    }

    return setCache(false);
  } catch (err) {
    console.error("[bibleBookMetadata] bookMatchesQuery failed:", err);
    return false;
  }
}

/**
 * Returns the provided book names sorted by their canonical Bible order,
 * derived directly from the numeric `book` field already present on every
 * verse object (e.g. "5 31:6" → book index 5 = Deuteronomy).
 *
 * This is reliable regardless of whether the Bible data includes a
 * metadata.index field, because the verse-level book number IS the
 * canonical order. Falls back to alphabetical sort if bibleData is absent.
 */
export async function getOrderedBooks(bookNames, bibleData = null, versionId = null) {
  if (!bookNames || bookNames.length === 0) return [];

  if (bibleData) {
    try {
      const verses = bibleData.verses || bibleData;
      if (Array.isArray(verses)) {
        // Single pass: build bookName → numeric book index from verse data.
        // The `book` field is the same numeric prefix used in stored references
        // (e.g. "5 31:6"), so this always reflects true canonical order.
        const bookNumberMap = new Map();
        for (const v of verses) {
          const name = v?.book_name || v?.book_name;
          const num = Number(v?.book);
          if (name && Number.isFinite(num) && !bookNumberMap.has(name)) {
            bookNumberMap.set(name, num);
          }
        }

        if (bookNumberMap.size > 0) {
          return [...bookNames].sort((a, b) => {
            const ia = bookNumberMap.get(a) ?? 999;
            const ib = bookNumberMap.get(b) ?? 999;
            return ia - ib;
          });
        }
      }
    } catch (err) {
      console.error("[bibleBookMetadata] getOrderedBooks failed:", err);
    }
  }

  return [...bookNames].sort(); // alphabetical fallback
}

/**
 * Returns the display name for a book in the requested language.
 * Falls back to the raw book name if metadata is unavailable.
 */
export async function getBookDisplayName(bookName, bibleData = null, versionLanguage = "english", versionId = null) {
  if (!bookName) return bookName;

  try {
    if (bibleData) {
      const metadata = await getBookMetadataFromData(bibleData, bookName, versionId);
      if (metadata) {
        if (versionLanguage === "kannada" || versionLanguage === "kn") {
          return metadata.kannada || bookName;
        }
        return metadata.english || bookName;
      }
    }
  } catch (err) {
    console.error("[bibleBookMetadata] getBookDisplayName failed:", err);
  }

  return bookName;
}

/**
 * Clears all in-memory caches. Call when the user logs out or when a version
 * is deleted/updated so stale data cannot be served.
 */
export function clearBookMetadataCache(versionId = null) {
  if (versionId) {
    metadataCache.delete(versionId);
    // Clear bookMatchCache entries that belong to this version
    for (const key of bookMatchCache.keys()) {
      if (key.endsWith(`:${versionId}`)) bookMatchCache.delete(key);
    }
  } else {
    metadataCache.clear();
    bookMatchCache.clear();
  }
}
